import React, { useEffect, useRef, useState } from "react";
import { Volume2, Loader2, Play } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const VOICES = [
  { id: "sage",  label: "Sage",  hint: "Warm · female",   sample: "Hi, I'm Sage. Let's run your week together." },
  { id: "river", label: "River", hint: "Calm · female",   sample: "Hi, I'm River. Tap a name to hear the voice." },
  { id: "atlas", label: "Atlas", hint: "Deep · male",     sample: "Hi, I'm Atlas. I'll keep things steady." },
  { id: "finn",  label: "Finn",  hint: "Neutral · male",  sample: "Hi, I'm Finn. Let's get the day sorted." },
];

const STORAGE_KEY = "kinly.tts_voice";

/**
 * VoicePickerCard — chooses which voice Kinly uses when "Voice on" is enabled
 * in the chat header. Saves to localStorage immediately. Each voice has a
 * Play-sample button that hits the same /kinly/tts endpoint Talk uses.
 */
export function VoicePickerCard() {
  const { family } = useAuth();
  const [selected, setSelected] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || "sage"; } catch { return "sage"; }
  });
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => () => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch { /* ignore */ }
    }
  }, []);

  const pick = (id) => {
    setSelected(id);
    try { localStorage.setItem(STORAGE_KEY, id); } catch { /* ignore */ }
  };

  const playSample = async (v) => {
    if (!family?.id || playing) return;
    setPlaying(v.id);
    try {
      const { data } = await api.post(`/families/${family.id}/kinly/tts`, {
        text: v.sample, voice: v.id,
      });
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch { /* ignore */ }
      }
      const audio = new Audio(`data:audio/mp3;base64,${data.audio_b64}`);
      audioRef.current = audio;
      audio.onended = () => setPlaying(null);
      audio.onerror = () => setPlaying(null);
      await audio.play();
    } catch {
      setPlaying(null);
    }
  };

  return (
    <article className="kinly-card" data-testid="voice-picker-card">
      <div className="flex items-start gap-2 mb-3">
        <Volume2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="font-display text-[15px] font-medium leading-tight">Kinly's voice</p>
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
            When "Voice on" is enabled in chat, Kinly will read replies in this voice.
          </p>
        </div>
      </div>
      <ul className="space-y-1.5">
        {VOICES.map((v) => {
          const active = selected === v.id;
          const isPlaying = playing === v.id;
          return (
            <li
              key={v.id}
              data-testid={`voice-row-${v.id}`}
              className={[
                "flex items-center gap-2.5 rounded-xl border p-2.5 transition-colors",
                active ? "border-primary/40 bg-primary/[0.06]" : "border-border bg-background",
              ].join(" ")}
            >
              <button
                type="button"
                onClick={() => pick(v.id)}
                className="flex-1 flex items-center gap-2.5 text-left tap"
                aria-pressed={active}
                data-testid={`voice-select-${v.id}`}
              >
                <span
                  className={[
                    "w-4 h-4 rounded-full border-2 shrink-0 transition-colors",
                    active ? "border-primary bg-primary" : "border-border",
                  ].join(" ")}
                />
                <div className="min-w-0">
                  <p className="text-[14px] font-medium leading-tight">{v.label}</p>
                  <p className="text-[11px] text-muted-foreground">{v.hint}</p>
                </div>
              </button>
              <button
                type="button"
                data-testid={`voice-sample-${v.id}`}
                onClick={() => playSample(v)}
                disabled={!!playing}
                aria-label={`Play ${v.label} sample`}
                className="w-8 h-8 rounded-full bg-secondary text-foreground tap inline-flex items-center justify-center disabled:opacity-50 shrink-0"
              >
                {isPlaying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
              </button>
            </li>
          );
        })}
      </ul>
    </article>
  );
}
